import React from 'react';
import { Check, X, AlertCircle, Trash2 } from 'lucide-react';
import { ParsedContact, ImportStats } from '../lib/emailImportHelpers';

interface ImportPreviewProps {
  contacts: ParsedContact[];
  stats: ImportStats;
  onRemoveContact: (email: string) => void;
  onRemoveAllInvalid: () => void;
}

export function ImportPreview({
  contacts,
  stats,
  onRemoveContact,
  onRemoveAllInvalid,
}: ImportPreviewProps) {
  if (contacts.length === 0) {
    return null;
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'duplicate':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case 'invalid':
        return <X className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid':
        return 'bg-green-50 border-green-200';
      case 'duplicate':
        return 'bg-orange-50 border-orange-200';
      case 'invalid':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'valid':
        return 'Nieuw contact';
      case 'duplicate':
        return 'Duplicaat';
      case 'invalid':
        return 'Ongeldig';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Import Preview
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <p className="text-blue-700 font-medium">Totaal</p>
            <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
          </div>
          <div>
            <p className="text-green-700 font-medium">Nieuw</p>
            <p className="text-2xl font-bold text-green-600">{stats.valid}</p>
          </div>
          <div>
            <p className="text-orange-700 font-medium">Duplicaten</p>
            <p className="text-2xl font-bold text-orange-500">{stats.duplicates}</p>
          </div>
          <div>
            <p className="text-red-700 font-medium">Ongeldig</p>
            <p className="text-2xl font-bold text-red-600">{stats.invalid}</p>
          </div>
        </div>
      </div>

      {stats.invalid > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onRemoveAllInvalid}
            className="text-sm px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Verwijder alle ongeldige ({stats.invalid})
          </button>
        </div>
      )}

      <div className="max-h-96 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-4">
        {contacts.map((contact, index) => (
          <div
            key={`${contact.email}-${index}`}
            className={`p-3 rounded-lg border ${getStatusColor(contact.status)} flex items-start justify-between gap-3`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {getStatusIcon(contact.status)}
                <span className="font-medium text-gray-900 truncate">
                  {contact.email}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  contact.status === 'valid'
                    ? 'bg-green-100 text-green-700'
                    : contact.status === 'duplicate'
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {getStatusText(contact.status)}
                </span>
              </div>
              {contact.name && (
                <p className="text-sm text-gray-600 truncate">{contact.name}</p>
              )}
              {contact.errorMessage && (
                <p className="text-xs text-red-600 mt-1">{contact.errorMessage}</p>
              )}
            </div>
            <button
              onClick={() => onRemoveContact(contact.email)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-white rounded transition-colors flex-shrink-0"
              title="Verwijder uit import"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {stats.valid === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-yellow-900 mb-1">Geen nieuwe contacten om te importeren</p>
              <p className="text-sm text-yellow-700">
                Alle gevonden email adressen zijn duplicaten of ongeldig. Controleer je import data.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
